import { useState } from "react";
import { GripHorizontal } from "lucide-react";
import type { KbWikiProgress } from "@/lib/kb-api";
import { KB_COPY } from "@/lib/kb-copy";
import {
  formatKbLastUpdated,
  kbProgressNextHint,
  kbProgressStageIndex,
  KB_PROGRESS_STAGES,
} from "@/lib/kb-progress";
import { cn } from "@/lib/utils";

const EXPANDED_STORAGE_KEY = "c456.kb.progressStrip.expanded";

type KbProgressStripProps = {
  progress: KbWikiProgress | null;
  loading?: boolean;
};

function readExpandedPreference(): boolean {
  try {
    const raw = window.localStorage.getItem(EXPANDED_STORAGE_KEY);
    if (raw === "1" || raw === "true") return true;
    if (raw === "0" || raw === "false") return false;
  } catch {
    /* 忽略隐私模式等读写失败 */
  }
  return false;
}

function writeExpandedPreference(expanded: boolean) {
  try {
    window.localStorage.setItem(EXPANDED_STORAGE_KEY, expanded ? "1" : "0");
  } catch {
    /* 忽略隐私模式等读写失败 */
  }
}

/**
 * 工作台进度条：视觉对齐 BrandHub「最佳实践成熟度」条，指标来自服务端 ProgressStats。
 * 默认折叠为单行；底部把手展开看完整统计。
 */
export function KbProgressStrip({ progress, loading = false }: KbProgressStripProps) {
  const [expanded, setExpanded] = useState(() =>
    typeof window === "undefined" ? false : readExpandedPreference(),
  );

  if (loading && !progress) {
    return (
      <div className="rounded-md border border-border bg-muted/30 px-3 py-2.5 text-xs text-muted-foreground">
        正在汇总知识维护进度…
      </div>
    );
  }

  if (!progress) return null;

  const percent = Math.min(100, Math.max(0, progress.richness_score));
  const stageIndex = kbProgressStageIndex(percent);
  const nextHint = kbProgressNextHint(progress);
  const lastUpdated = formatKbLastUpdated(progress.last_updated_at);

  function toggleExpanded() {
    setExpanded((prev) => {
      const next = !prev;
      writeExpandedPreference(next);
      return next;
    });
  }

  return (
    <div className="relative rounded-md border border-border bg-muted/30 px-3 py-2.5">
      {expanded ? (
        <>
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <p className="text-xs font-medium text-foreground">{KB_COPY.progressTitle}</p>
            {nextHint ? (
              <p className="text-[11px] text-muted-foreground">
                下一关 · <span className="font-medium text-foreground">{nextHint}</span>
              </p>
            ) : (
              <p className="text-[11px] font-medium text-primary">{KB_COPY.progressDone}</p>
            )}
          </div>

          <div className="mt-2 space-y-1.5">
            <div className="flex items-baseline justify-between gap-3 text-[11px] text-muted-foreground">
              <span>
                {KB_COPY.progressRichness}
                <span className="ml-1 font-normal text-muted-foreground/80">
                  （{KB_COPY.progressRichnessHint}）
                </span>
              </span>
              <span className="tabular-nums">
                <span className="font-medium text-foreground">{percent}%</span>
                {" · "}
                {progress.schema_fields_filled}/{progress.schema_fields_total} 要点已填
              </span>
            </div>
            <RichnessBar percent={percent} />
            <div className="flex justify-between text-[10px] text-muted-foreground">
              {KB_PROGRESS_STAGES.map((label, index) => (
                <span
                  key={label}
                  className={cn(index === stageIndex && "font-medium text-foreground")}
                >
                  {index === stageIndex ? `← ${label}` : label}
                </span>
              ))}
            </div>
          </div>

          <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1.5 border-t border-border/70 pt-2 sm:grid-cols-4">
            <Metric
              label={KB_COPY.progressSeed}
              value={`${progress.seed_pending} / ${progress.seed_completed}`}
              hint={KB_COPY.progressSeedHint}
            />
            <Metric
              label={KB_COPY.progressEntries}
              value={String(progress.entry_total)}
              hint="种子 + 自由条目"
            />
            <Metric
              label={KB_COPY.progressInbox}
              value={String(progress.inbox_pending)}
              hint={progress.inbox_pending > 0 ? "待提取或失败" : "暂无积压"}
            />
            <Metric
              label={KB_COPY.progressUpdated}
              value={lastUpdated ?? "—"}
              hint="条目最近写入时间"
            />
          </dl>
        </>
      ) : (
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="shrink-0 text-xs font-medium text-foreground">
            {KB_COPY.progressTitle}
          </span>
          <div className="min-w-0 flex-1">
            <RichnessBar percent={percent} />
          </div>
          <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
            <span className="font-medium text-foreground">{percent}%</span>
            {nextHint ? (
              <>
                {" · "}
                <span className="text-muted-foreground">下一关 {nextHint}</span>
              </>
            ) : (
              <>
                {" · "}
                <span className="font-medium text-primary">{KB_COPY.progressDone}</span>
              </>
            )}
          </span>
        </div>
      )}

      <button
        type="button"
        onClick={toggleExpanded}
        aria-expanded={expanded}
        aria-label={expanded ? "收起进度详情" : "展开进度详情"}
        className={cn(
          "absolute left-1/2 z-10 flex h-4 w-10 -translate-x-1/2 items-center justify-center",
          "-bottom-2 rounded-b-md border border-t-0 border-border bg-muted shadow-sm",
          "text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        )}
      >
        <GripHorizontal className="size-3.5" aria-hidden />
      </button>
    </div>
  );
}

function RichnessBar({ percent }: { percent: number }) {
  return (
    <div className="relative h-2 rounded-full bg-muted">
      <div
        className="absolute inset-y-0 left-0 rounded-full bg-foreground/90"
        style={{ width: `${percent}%` }}
      />
      <div
        className="absolute top-1/2 z-10 -translate-x-1/2 -translate-y-1/2"
        style={{ left: `${Math.min(Math.max(percent, 2), 98)}%` }}
        aria-hidden
      >
        <div className="quest-progress-spark size-2.5 rounded-[2px] bg-foreground" />
      </div>
    </div>
  );
}

function Metric({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="min-w-0">
      <dt className="truncate text-[10px] text-muted-foreground">{label}</dt>
      <dd className="truncate text-sm font-medium tabular-nums text-foreground">{value}</dd>
      <p className="truncate text-[10px] text-muted-foreground">{hint}</p>
    </div>
  );
}
